import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  MenuItem,
  Grid
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';
import SearchIcon from '@mui/icons-material/Search';
import SpeedIcon from '@mui/icons-material/Speed';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import Can from '../../components/Can';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BlockIcon from '@mui/icons-material/Block';
import CircularProgress from '@mui/material/CircularProgress';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import { facturaPosPath, pedidoPosPath, ROUTES } from '../../config/routes';
import { useSnackbar } from 'notistack';
import cotizacionVentaService, { CotizacionVenta } from '../../services/cotizacionVentaService';
import PDFViewer from '../../components/shared/PDFViewer';
import DocumentosKpisBar from '../../components/shared/DocumentosKpisBar';
import ImportarCarpetaCotizacionesDialog from '../../components/cotizacionventa/ImportarCarpetaCotizacionesDialog';
import { DocumentosKpis, emptyDocumentosKpis } from '../../utils/documentosKpis';
import { formatCurrency, formatClienteConNro } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatFechaVencimDisplay } from '../../utils/facturaVentaFechas';
import { useGridColumns, withColumnSx, type GridColumnDef } from '../../hooks/useGridColumns';
import { descubrirColumnasDeItems, presentarCampoGrilla } from '../../utils/gridFieldValue';
import {
  AccionesColumnHeader,
  PersonalizarColumnasDialog,
} from '../../components/shared/PersonalizarColumnasDialog';
import type { SxProps, Theme } from '@mui/material/styles';
import { useRegisterPageTour } from '../../components/tour/PageTourContext';
import { COTIZACIONES_LISTADO_TOUR } from '../../config/tours/cotizacionesVentaTour';

type CotizacionColumnId =
  | 'fecha'
  | 'vencimiento'
  | 'comprobante'
  | 'cliente'
  | 'importe'
  | 'estado'
  | 'acciones';

const COTIZACIONES_COLUMNAS: GridColumnDef<CotizacionColumnId>[] = [
  { id: 'fecha', label: 'Fecha' },
  { id: 'vencimiento', label: 'Vencimiento' },
  { id: 'comprobante', label: 'Comprobante', sx: { display: { xs: 'none', lg: 'table-cell' }, minWidth: 110 } },
  { id: 'cliente', label: 'Cliente' },
  { id: 'importe', label: 'Importe Total', align: 'right' },
  { id: 'estado', label: 'Estado' },
  { id: 'acciones', label: 'Acciones', align: 'center', alwaysVisible: true },
];

const Cotizaciones: React.FC = () => {
  useRegisterPageTour(COTIZACIONES_LISTADO_TOUR);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();

  const [items, setItems] = useState<CotizacionVenta[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedCotizacion, setSelectedCotizacion] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; cotizacionId: number } | null>(null);
  const [kpis, setKpis] = useState<DocumentosKpis>(emptyDocumentosKpis);
  const [importarCarpetaOpen, setImportarCarpetaOpen] = useState(false);
  const {
    columnasVisibles,
    columnasAnchorEl,
    setColumnasAnchorEl,
    gridColumns,
    toggleColumnaVisible,
    moverColumna,
    reordenarColumnas,
    agregarColumna,
    restablecerColumnas,
    columnasDefinidas,
  } = useGridColumns({
    gridKey: 'cotizaciones',
    columns: COTIZACIONES_COLUMNAS,
  });
  const columnasParaAgregar = useMemo(
    () => descubrirColumnasDeItems(items, gridColumns.columnOrder),
    [gridColumns.columnOrder, items],
  );

  const fetchCotizaciones = useCallback(async (
    pageNumber: number,
    pageSize: number,
    search: string,
    estado: string,
    desde: string,
    hasta: string,
    nroSuc: number | null
  ) => {
    try {
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const params: Parameters<typeof cotizacionVentaService.getCotizacionesVenta>[0] = {
        pageNumber: pageNumber + 1,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde || undefined,
        fechaHasta: hasta || undefined,
        nroSuc: nroSuc != null && nroSuc > 0 ? nroSuc : undefined,
      };
      if (estado) params.estado = estado;

      const response = await cotizacionVentaService.getCotizacionesVenta(params);
      setItems(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      const message = getApiErrorMessage(error, 'Error al cargar las cotizaciones');
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
    }
  }, [enqueueSnackbar]);

  const fetchKpis = useCallback(async (
    search: string,
    estado: string,
    desde: string,
    hasta: string,
    nroSuc: number | null
  ): Promise<DocumentosKpis> => {
    const params: Parameters<typeof cotizacionVentaService.getCotizacionesVentaKpis>[0] = {
      searchTerm: search || undefined,
      fechaDesde: desde || undefined,
      fechaHasta: hasta || undefined,
      nroSuc: nroSuc != null && nroSuc > 0 ? nroSuc : undefined,
    };
    if (estado) params.estado = estado;
    return cotizacionVentaService.getCotizacionesVentaKpis(params);
  }, []);

  useEffect(() => {
    if (!filtroListo) return;
    fetchCotizaciones(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroNroSuc);
  }, [page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroNroSuc, filtroListo, fetchCotizaciones]);

  useEffect(() => {
    if (!filtroListo) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchKpis(searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroNroSuc);
        if (!cancelled) setKpis(result);
      } catch (error: any) {
        if (cancelled) return;
        setKpis(emptyDocumentosKpis());
        const message = getApiErrorMessage(error, 'Error al calcular los KPI de cotizaciones');
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroNroSuc, filtroListo, fetchKpis, enqueueSnackbar]);

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    const validRowsPerPage = Math.max(1, Math.min(20, newRowsPerPage));
    setRowsPerPage(validRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNuevaCotizacion = () => navigate(ROUTES.ventas.cotizaciones.pos);

  const handleCotizacionClick = (cotizacionId: number) => {
    navigate(`/ventas/cotizaciones/${cotizacionId}/ver`);
  };

  const _handleEdit = (cotizacionId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(`/ventas/cotizaciones/${cotizacionId}/editar`);
  };

  const handleAbrirCotizacionPos = (cotizacionId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    navigate(`/ventas/cotizaciones/${cotizacionId}/editar`);
  };

  const handleFacturar = (cotizacionId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(facturaPosPath({ cotizacionId }));
  };

  const handlePreparar = (cotizacionId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(pedidoPosPath({ cotizacionId }));
  };

  const handleAnular = async (cotizacionId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (window.confirm('¿Está seguro que desea anular esta cotización?')) {
      try {
        await cotizacionVentaService.anularCotizacion(cotizacionId);
        enqueueSnackbar('Cotización anulada correctamente', { variant: 'success' });
        fetchCotizaciones(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroNroSuc);
      } catch (error: unknown) {
        const message = getApiErrorMessage(error, 'Error al anular la cotización');
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
      }
    }
  };

  const handleImprimirPDF = async (cotizacionId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      setPdfLoading(cotizacionId);
      const blob = await cotizacionVentaService.getCotizacionVentaPDF(cotizacionId);
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedCotizacion(cotizacionId);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al generar el PDF de la cotización';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setSelectedCotizacion(null);
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, cotizacionId: number) => {
    event.stopPropagation();
    setKebabAnchor({ el: event.currentTarget, cotizacionId });
  };

  const handleKebabClose = () => setKebabAnchor(null);
  const currentKebabCotizacion = kebabAnchor
    ? items.find((c) => c.nroFormulario === kebabAnchor.cotizacionId)
    : null;

  const calcularTotal = (cotizacion: CotizacionVenta): number => {
    if (cotizacion.importeTotal !== undefined && cotizacion.importeTotal !== null) {
      return cotizacion.importeTotal;
    }
    return cotizacion.detalles?.reduce((sum, d) => {
      const subtotal = d.subtotal || (d.precioUnitario || 0) * (d.cantidad || 0);
      return sum + subtotal;
    }, 0) || 0;
  };

  const esCotizacionEditable = (cotizacion: CotizacionVenta): boolean => {
    const estado = cotizacion.estado || 'Nuevo';
    return estado !== 'Facturado' && estado !== 'Preparado' && estado !== 'Anulado';
  };

  const puedeEditarOAnular = (cotizacion: CotizacionVenta): boolean => {
    const estado = cotizacion.estado || 'Nuevo';
    return estado !== 'Facturado' && estado !== 'Preparado' && estado !== 'Anulado';
  };

  const puedeFacturar = (cotizacion: CotizacionVenta): boolean => {
    const estado = cotizacion.estado || 'Nuevo';
    return estado !== 'Facturado' && estado !== 'Anulado';
  };

  const puedePreparar = (cotizacion: CotizacionVenta): boolean => {
    const estado = cotizacion.estado || 'Nuevo';
    return estado !== 'Preparado' && estado !== 'Anulado' && estado !== 'Facturado';
  };

  const getEstadoChip = (cotizacion: CotizacionVenta) => {
    const estado = cotizacion.estado || 'Nuevo';
    const estadoMap: Record<string, { label: string; bgColor: string; textColor: string }> = {
      'Facturado': { label: 'Facturado', bgColor: '#C6EEBB', textColor: '#000000' },
      'Preparado': { label: 'Preparado', bgColor: '#FFFF00', textColor: '#000000' },
      'Anulado': { label: 'Anulado', bgColor: '#FFCDD2', textColor: '#000000' },
      'Impreso': { label: 'Impreso', bgColor: '#FF80FF', textColor: '#000000' },
      'Nuevo': { label: 'Nuevo', bgColor: '#FFFFD7', textColor: '#000000' }
    };
    return estadoMap[estado] || { label: estado, bgColor: '#FFFFD7', textColor: '#000000' };
  };

  const formatComprobante = (cotizacion: CotizacionVenta & { letra?: string; nroSuc?: number; nroComprobante?: number }) => {
    const letra = (cotizacion as any).letra;
    const nroSuc = (cotizacion as any).nroSuc;
    const nroComprobante = (cotizacion as any).nroComprobante;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) return '-';
    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      return (!Number.isNaN(numeric) ? numeric.toString() : String(value)).padStart(length, '0');
    };
    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();
    if (!letraNormalizada) return '-';
    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  const renderKebabCotizacionItems = (cotizacion: CotizacionVenta) => {
    const menuItems: React.ReactElement[] = [];
    menuItems.push(
      <MenuItem key="ver" onClick={() => { handleKebabClose(); handleCotizacionClick(cotizacion.nroFormulario); }}>
        <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Ver detalle</ListItemText>
      </MenuItem>
    );
    if (esCotizacionEditable(cotizacion)) {
      menuItems.push(
        <Can key="editar" permiso="ventas.cotizaciones.editar">
          <MenuItem onClick={() => { handleKebabClose(); navigate(`/ventas/cotizaciones/${cotizacion.nroFormulario}/editar`); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>
        </Can>
      );
    }
    if (puedeEditarOAnular(cotizacion)) {
      menuItems.push(
        <Can key="anular" permiso="ventas.cotizaciones.anular">
          <MenuItem onClick={() => { handleKebabClose(); void handleAnular(cotizacion.nroFormulario); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Anular</ListItemText>
          </MenuItem>
        </Can>
      );
    }
    return menuItems;
  };

  const renderCeldaCotizacion = (
    columnId: string,
    cotizacion: CotizacionVenta,
    colSx: SxProps<Theme> | undefined,
  ) => {
    switch (columnId) {
      case 'fecha':
        return <TableCell key={columnId} sx={colSx}>{new Date(cotizacion.fechaEmision).toLocaleDateString()}</TableCell>;
      case 'vencimiento':
        return (
          <TableCell key={columnId} sx={colSx}>
            {formatFechaVencimDisplay(cotizacion.fechaVencim ?? cotizacion.fechaEmision)}
          </TableCell>
        );
      case 'comprobante':
        return (
          <TableCell key={columnId} sx={colSx}>
            <Typography
              component="span"
              sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
              onClick={(e) => handleAbrirCotizacionPos(cotizacion.nroFormulario, e)}
            >
              {formatComprobante(cotizacion)}
            </Typography>
          </TableCell>
        );
      case 'cliente':
        return (
          <TableCell key={columnId} sx={withColumnSx({ fontWeight: 600 }, colSx)}>
            {formatClienteConNro(cotizacion.nroCliente, cotizacion.nombre)}
          </TableCell>
        );
      case 'importe':
        return (
          <TableCell key={columnId} align="right" sx={withColumnSx({ fontWeight: 600, color: '#1976d2' }, colSx)}>
            {formatCurrency(calcularTotal(cotizacion))}
          </TableCell>
        );
      case 'estado': {
        const estadoChip = getEstadoChip(cotizacion);
        return (
          <TableCell key={columnId} sx={colSx}>
            <Chip
              label={estadoChip.label}
              size="small"
              sx={{ bgcolor: estadoChip.bgColor, color: estadoChip.textColor, fontWeight: 500, borderRadius: 1.5, minWidth: 90 }}
            />
          </TableCell>
        );
      }
      case 'acciones':
        return <TableCell key={columnId} align="center" sx={colSx}>{renderActionButtons(cotizacion)}</TableCell>;
      default: {
        const campo = presentarCampoGrilla(cotizacion, columnId);
        return (
          <TableCell key={columnId} align={campo.align} sx={colSx}>
            {campo.texto}
          </TableCell>
        );
      }
    }
  };

  const renderActionButtons = (cotizacion: CotizacionVenta) => {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        justifyContent="center"
        flexWrap="wrap"
        {...(cotizacion.nroFormulario === items[0]?.nroFormulario
          ? { 'data-tour-id': 'cotizaciones-acciones' }
          : {})}
      >
        {cotizacion.estado !== 'Anulado' && (
          <Can permiso="ventas.cotizaciones.imprimir">
            <Tooltip title="Imprimir PDF">
              <IconButton size="small" onClick={(e) => handleImprimirPDF(cotizacion.nroFormulario, e)} disabled={pdfLoading === cotizacion.nroFormulario}
                sx={{ bgcolor: '#2196f3', color: '#fff', borderRadius: 1.5, width: 32, height: 32, '&:hover': { bgcolor: '#1976d2', transform: 'scale(1.1)' }, '&:disabled': { bgcolor: '#ccc' }, transition: 'all 0.2s' }}>
                {pdfLoading === cotizacion.nroFormulario ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PrintIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Can>
        )}
        {puedePreparar(cotizacion) && (
          <Can permiso="ventas.pedidos.nuevo">
          <Tooltip title="Preparar cotización">
            <IconButton size="small" onClick={(e) => handlePreparar(cotizacion.nroFormulario, e)}
              sx={{ bgcolor: '#9c27b0', color: '#fff', borderRadius: 1.5, width: 32, height: 32, '&:hover': { bgcolor: '#7b1fa2', transform: 'scale(1.1)' }, transition: 'all 0.2s' }}>
              <Inventory2Icon fontSize="small" />
            </IconButton>
          </Tooltip>
          </Can>
        )}
        {puedeFacturar(cotizacion) && (
          <Can permiso="ventas.facturas.nuevo">
          <Tooltip title="Facturar">
            <IconButton size="small" onClick={(e) => handleFacturar(cotizacion.nroFormulario, e)}
              sx={{ bgcolor: '#4caf50', color: '#fff', borderRadius: 1.5, width: 32, height: 32, '&:hover': { bgcolor: '#45a049', transform: 'scale(1.1)' }, transition: 'all 0.2s' }}>
              <ReceiptIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          </Can>
        )}
        <Tooltip title="Más acciones">
          <IconButton size="small" onClick={(e) => handleKebabOpen(e, cotizacion.nroFormulario)}
            sx={{ borderRadius: 1.5, width: 32, height: 32 }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>Cotizaciones</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Can permiso="ventas.cotizaciones.importar-pdf">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FolderOpenIcon />}
              onClick={() => setImportarCarpetaOpen(true)}
              data-tour-id="cotizaciones-importar"
              sx={{
                borderRadius: 2,
                px: 2.5,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Importar carpeta
            </Button>
            </Can>
            <Can permiso="ventas.cotizaciones.nuevo">
            <Button
              variant="contained"
              color="primary"
              startIcon={<SpeedIcon />}
              onClick={handleNuevaCotizacion}
              data-tour-id="cotizaciones-nueva-pos"
              sx={{
                borderRadius: 2,
                px: 2.5,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' },
              }}
            >
              Nueva Cotización POS
            </Button>
            </Can>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }} data-tour-id="cotizaciones-filtros">
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth placeholder="Buscar por número, comprobante, nombre o cliente..."
                  value={searchTerm} onChange={handleSearchChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa', '&:hover': { bgcolor: '#f5f5f5' }, '&.Mui-focused': { bgcolor: '#ffffff' } } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#666' }} /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <SearchableSelect
                  label="Estado"
                  allowEmpty
                  emptyOptionLabel="Todos"
                  value={filtroEstado || null}
                  onChange={(v) => { setFiltroEstado(v ?? ''); setPage(0); }}
                  options={[
                    { value: 'Nuevo', label: 'Nuevo' },
                    { value: 'Impreso', label: 'Impreso' },
                    { value: 'Facturado', label: 'Facturado' },
                    { value: 'Preparado', label: 'Preparado' },
                    { value: 'Anulado', label: 'Anulado' },
                  ]}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': { bgcolor: '#f5f5f5' },
                      '&.Mui-focused': { bgcolor: '#ffffff' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth label="Fecha Desde" type="date" value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField fullWidth label="Fecha Hasta" type="date" value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }}
                  InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FiltroSucursalVentaSelect
                  value={filtroNroSuc}
                  onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                  loading={loadingSucursales}
                  sucursales={sucursales}
                />
              </Grid>
            </Grid>
          </Box>

          <Box data-tour-id="cotizaciones-kpis">
            <DocumentosKpisBar
              cantidad={kpis.cantidad || totalRecords}
              cantidadLabel="cotizaciones"
              precioTotal={kpis.precioTotal}
              precioNeto={kpis.precioNeto}
              pesoTotalKg={kpis.pesoTotalKg}
              volumenTotalM3={kpis.volumenTotalM3}
            />
          </Box>

          {isMobile ? (
            <Box data-tour-id="cotizaciones-grilla">
              {items.map((cotizacion) => {
                const estadoChip = getEstadoChip(cotizacion);
                return (
                  <Card key={cotizacion.nroFormulario} sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e0e0', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' } }}>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography
                            component="span"
                            variant="subtitle1"
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              fontWeight: 600,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={(event) => handleAbrirCotizacionPos(cotizacion.nroFormulario, event)}
                          >
                            {formatComprobante(cotizacion) !== '-' ? formatComprobante(cotizacion) : 'Cotización'}
                          </Typography>
                          <Chip label={estadoChip.label} size="small" sx={{ bgcolor: estadoChip.bgColor, color: estadoChip.textColor, fontWeight: 500, borderRadius: 1.5 }} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">Fecha: {new Date(cotizacion.fechaEmision).toLocaleDateString()}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Vencimiento: {formatFechaVencimDisplay(cotizacion.fechaVencim ?? cotizacion.fechaEmision)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Cliente: {formatClienteConNro(cotizacion.nroCliente, cotizacion.nombre)}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>Importe Total: {formatCurrency(calcularTotal(cotizacion))}</Typography>
                        {renderActionButtons(cotizacion)}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              <TablePagination rowsPerPageOptions={[5, 10, 20]} component="div" count={totalRecords} rowsPerPage={rowsPerPage} page={page}
                onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Filas por página" />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}
              data-tour-id="cotizaciones-grilla"
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    {columnasVisibles.map((col) => (
                      <TableCell key={col.id} align={col.align} sx={withColumnSx({ fontWeight: 600 }, col.sx)}>
                        {col.id === 'acciones' ? (
                          <AccionesColumnHeader label={col.label} onCustomize={(e) => setColumnasAnchorEl(e.currentTarget)} />
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((cotizacion) => (
                    <TableRow key={cotizacion.nroFormulario} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                      {columnasVisibles.map((col) => renderCeldaCotizacion(col.id, cotizacion, col.sx))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination rowsPerPageOptions={[5, 10, 20]} component="div" count={totalRecords} rowsPerPage={rowsPerPage} page={page}
                onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Filas por página" sx={{ borderTop: '1px solid #e0e0e0' }} />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Menu
        anchorEl={kebabAnchor?.el}
        open={Boolean(kebabAnchor)}
        onClose={handleKebabClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {currentKebabCotizacion && renderKebabCotizacionItems(currentKebabCotizacion)}
      </Menu>

      {pdfUrl && selectedCotizacion && (
        <PDFViewer pdfUrl={pdfUrl} onClose={handleClosePDF} title={`Cotización #${selectedCotizacion}`} documentId={`${selectedCotizacion}`} documentType="cotizacion" />
      )}

      <ImportarCarpetaCotizacionesDialog
        open={importarCarpetaOpen}
        onClose={() => setImportarCarpetaOpen(false)}
        onDone={() => {
          void fetchCotizaciones(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroNroSuc);
        }}
        sucursales={(sucursales || []).map((s) => ({
          nroSuc: Number(s.nroSuc),
          nombre: s.nombreSucursal,
        }))}
        nroSucInicial={filtroNroSuc != null && filtroNroSuc > 0 ? filtroNroSuc : undefined}
      />
      <PersonalizarColumnasDialog
        anchorEl={columnasAnchorEl}
        onClose={() => setColumnasAnchorEl(null)}
        onReorder={reordenarColumnas}
        columns={columnasDefinidas}
        columnOrder={gridColumns.columnOrder}
        visibleIds={gridColumns.visibleIds}
        onToggle={toggleColumnaVisible}
        onMove={moverColumna}
        onReset={restablecerColumnas}
        addableColumns={columnasParaAgregar}
        onAdd={agregarColumna}
      />
    </Box>
  );
};

export default Cotizaciones;
